import { formatDate, metricsPeriodDateKeys } from '@/utils/date_utils';
import { changePct } from './changePct';
import { COLOR_CONVERTED, COLOR_GENERATED, COLOR_LEADS } from './constants';
import type {
    LineSeries,
    MaterialChartRow,
    PeriodKey,
    QuotingByMaterialType,
    SeriesFilter,
    TimelinePoint,
    TrendInfo,
    UserBarRow,
    UserPeriodCounts,
} from './types';

export function formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(value);
}

const formatTimelineLabel = (date: string, period: PeriodKey) => {
    if (period === 'all_time') {
        return formatDate(`${date}-01`, 'MMM yyyy');
    }
    return formatDate(date, 'MMM d');
};

const fillTimeline = (points: TimelinePoint[], period: PeriodKey): TimelinePoint[] => {
    const byDate = new Map(points.map(point => [point.date, point]));
    return metricsPeriodDateKeys(period).map(
        date => byDate.get(date) ?? { date, generated: 0, converted: 0, leads_converted: 0 }
    );
};

export const toLineData = (
    points: TimelinePoint[],
    period: PeriodKey,
    seriesFilter: SeriesFilter
): LineSeries[] => {
    const filled = fillTimeline(points, period);
    const labels = filled.map(point => formatTimelineLabel(point.date, period));
    const series: LineSeries[] = [];
    if (seriesFilter === 'both' || seriesFilter === 'generated') {
        series.push({
            id: 'Generated',
            data: filled.map((point, i) => ({ x: labels[i], y: point.generated })),
        });
    }
    if (seriesFilter === 'both' || seriesFilter === 'converted') {
        series.push({
            id: 'Converted',
            data: filled.map((point, i) => ({ x: labels[i], y: point.converted })),
        });
    }
    if (seriesFilter === 'both' || seriesFilter === 'leads') {
        series.push({
            id: 'Leads',
            data: filled.map((point, i) => ({
                x: labels[i],
                y: point.leads_converted ?? 0,
            })),
        });
    }
    return series;
};

export const toUserBarData = (
    rows: UserPeriodCounts[],
    seriesFilter: SeriesFilter
): UserBarRow[] =>
    rows.map(row => {
        const item: UserBarRow = { user: row.user_name };
        if (seriesFilter === 'both' || seriesFilter === 'generated') {
            item.Generated = row.generated;
        }
        if (seriesFilter === 'both' || seriesFilter === 'converted') {
            item.Converted = row.converted;
        }
        return item;
    });

export const seriesColorsFor = (seriesFilter: SeriesFilter): string[] => {
    if (seriesFilter === 'generated') return [COLOR_GENERATED];
    if (seriesFilter === 'converted') return [COLOR_CONVERTED];
    if (seriesFilter === 'leads') return [COLOR_LEADS];
    return [COLOR_GENERATED, COLOR_CONVERTED, COLOR_LEADS];
};

export const barKeysFor = (seriesFilter: SeriesFilter): string[] => {
    if (seriesFilter === 'generated') return ['Generated'];
    if (seriesFilter === 'converted') return ['Converted'];
    if (seriesFilter === 'leads') return [];
    return ['Generated', 'Converted'];
};

export const trendCopy = (
    current: number,
    previous: number,
    priorLabel: string,
    emptyMessage: string
): TrendInfo => {
    if (current === 0 && previous === 0) {
        return { text: emptyMessage, direction: 'empty' };
    }
    const pct = changePct(current, previous);
    if (pct === null) {
        return { text: emptyMessage, direction: 'empty' };
    }
    if (pct === 0) {
        return { text: `No change vs ${priorLabel}`, direction: 'flat' };
    }
    const sign = pct > 0 ? '+' : '';
    return {
        text: `${sign}${pct}% vs ${priorLabel}`,
        direction: pct > 0 ? 'up' : 'down',
    };
};

export const roofTypeTotal = (data: QuotingByMaterialType | undefined): number => {
    if (!data) return 0;
    return data.total; // Use explicit total field (distinct project count)
};

export const toMaterialChartData = (
    materials: QuotingByMaterialType | undefined,
    // Flat roofs break down by deck substrate (Wood/Concrete/Metal); sloped and mixed by desired slope.
    roofType: 'sloped_roof' | 'flat_roof' | 'mixed_roof' = 'sloped_roof'
): MaterialChartRow[] => {
    if (!materials) return [];
    if (roofType === 'flat_roof') {
        return [
            { material: 'Wood', count: materials.wood },
            { material: 'Concrete', count: materials.concrete },
            { material: 'Metal', count: materials.metal },
        ];
    }
    return [
        { material: 'Metal', count: materials.metal },
        { material: 'Tile', count: materials.tile },
        { material: 'Shingle', count: materials.shingle },
    ];
};
